"use client";

import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  GetWalletParams,
  GetWalletResponse,
} from "@chipi-stack/types";
import { useChipiContext } from "../context";
import { ChipiApiError } from "@chipi-stack/shared";

type GetWalletInput = {
  params?: GetWalletParams;
  getBearerToken?: () => Promise<string | null | undefined>;
  queryOptions?: Omit<
    UseQueryOptions<(GetWalletResponse & { normalizedPublicKey: string }) | null, Error>,
    "queryKey" | "queryFn"
  >;
};

export function useGetWallet(input?: GetWalletInput) {
  const { chipiSDK } = useChipiContext();
  const queryClient = useQueryClient();

  const query = useQuery<
    (GetWalletResponse & { normalizedPublicKey: string }) | null,
    Error
  >({
    queryKey: ["wallet", input?.params?.externalUserId],
    queryFn: async () => {
      if (!input || !input.params || !input.getBearerToken)
        throw new Error("Input is required");
      const bearerToken = await input.getBearerToken();
      if (!bearerToken) throw new Error("Bearer token is required");
      return chipiSDK.getWallet(input.params, bearerToken);
    },
    enabled: Boolean(
      input?.params?.externalUserId && 
      input?.getBearerToken &&
      input.params.externalUserId.trim() !== ""
    ),
    retry: (failureCount: number, error: Error) => {
      // Don't retry on API errors (errors with status codes like 404, 400, etc.)
      if (error instanceof ChipiApiError || (error as any)?.status) {
        return false;
      }
      // Retry on network errors (no status code) up to 3 times
      return failureCount < 3;
    },
    ...input?.queryOptions,
  });

  const fetchWallet = async (newInput: GetWalletInput): Promise<(GetWalletResponse & { normalizedPublicKey: string }) | null> => {
    return queryClient.fetchQuery({
      queryKey: ["wallet", newInput?.params?.externalUserId],
      queryFn: async () => {
        if (!newInput || !newInput.getBearerToken || !newInput.params)
          throw new Error("Input is required");
        const bearerToken = await newInput.getBearerToken();
        if (!bearerToken) throw new Error("Bearer token is required");
        return chipiSDK.getWallet(newInput.params, bearerToken);
      },
      retry: (failureCount, error) => {
        // Don't retry on API errors (errors with status codes like 404, 400, etc.)
        if (error instanceof ChipiApiError || (error as any)?.status) {
          return false;
        }
        // Retry on network errors (no status code) up to 3 times
        return failureCount < 3;
      },
    });
  };

  return {
    ...query,
    fetchWallet,
  };
}